<template>
  <div class="transcript-container" v-show="user.speechToText">
    <div
      class="transcript"
      v-for="(transcript, index) in transcriptions"
      :key="index"
      ref="transcript"
    >
      <span class="username">{{ transcript.firstname }}: </span>
      {{ transcript.transcription }}
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import hark from "hark"
import * as moment from "moment"
import io from "socket.io-client"
import { Role } from "@/helpers"
import { ActionTypes as TwilioModuleActionTypes } from "@/store/TwilioModule"

const LIVE_SPEECH_SERVER = "afternoon-sands-78596.herokuapp.com/speech-to-text"
const SILENCE_TIME = 0.5 // 0.5 sec
const MAXIMUM_RECORDING_TIME = 5 * 1000 // 5 sec
const MAX_TRANSCRIPTIONS_LENGTH = 128
const TRANSCRIPTIONS_OFFSET = 64

const AudioContext = window.AudioContext || window.webkitAudioContext

const audioContext = new AudioContext({ sampleRate: 44100 })

export default {
  name: "Speech",
  data() {
    return {
      transcript: null,
      mediaRecorder: null,
      speaking: null,
      silenceTime: null,
      intervalID: null,
      recordingTime: null,
      recordingTimeIntervalId: null,
      transcriptions: [],
      localAudioTrack: null,
      stream: null
    }
  },
  computed: {
    ...mapGetters("speech", ["getSpeechAudios"]),
    ...mapGetters(["game", "user", "getCurrentMode", "onlineUsersArray"])
  },
  async created() {
    if (!this.localAudioTrack) {
      this.localAudioTrack = await this[
        TwilioModuleActionTypes.CREATE_LOCAL_AUDIO_TRACK
      ]()
    }

    this.stream = new MediaStream([this.localAudioTrack.mediaStreamTrack])

    this.startRecording()
    this.speech = hark(this.stream, {
      interval: 20,
      audioContext: audioContext
    })
    this.speech.on("speaking", () => {
      console.log("Speaking!")
      this.speaking = true
      this.silenceTime = null
      clearInterval(this.intervalID)
    })

    this.speech.on("stopped_speaking", () => {
      if (!this.silenceTime) {
        this.silenceTime = moment().unix()
      }

      console.log("Stopped speaking...")
      this.intervalID = setInterval(() => {
        console.log(
          "You are silence",
          moment().unix() - this.silenceTime,
          " of ",
          SILENCE_TIME
        )
        if (
          this.speaking === true &&
          this.silenceTime &&
          moment().unix() - this.silenceTime >= SILENCE_TIME
        ) {
          this.speaking = false
          clearInterval(this.intervalID)
        }
      }, 500)
    })
  },
  watch: {
    speaking(val) {
      if (val) {
        this.mediaRecorder.start()
        this.countRecordingTime()
      } else {
        this.mediaRecorder.stop()
        this.stopRecordingTime()
      }
    },
    recordingTime(time) {
      if (time >= MAXIMUM_RECORDING_TIME && this.speaking) {
        this.mediaRecorder.stop()
        this.mediaRecorder.start()
        this.countRecordingTime()
      }
    }
  },
  beforeDestroy() {
    try {
      this.socket.close()
    } catch (e) {}
    try {
      this.speech.stop()
    } catch (e) {}
    try {
      this.stopRecording()
    } catch (e) {}
    try {
      this.localAudioTrack.stop()
    } catch (e) {}
    clearInterval(this.intervalID)
  },
  updated() {
    if (this.user.speechToText) {
      try {
        this.$el.scrollTop = this.$el.lastElementChild.offsetTop
      } catch (e) {}
    }
  },
  methods: {
    ...mapActions("twilio", [TwilioModuleActionTypes.CREATE_LOCAL_AUDIO_TRACK]),
    startRecording() {
      const { id } = this.user
      this.socket = io(LIVE_SPEECH_SERVER, {
        path: "/speech",
        query: `roomID=${this.game.id}&userID=${id}`
      })
      this.mediaRecorder = new MediaRecorder(this.stream)

      this.socket.on("connect", () => {
        console.log("we are connected")
        this.mediaRecorder.ondataavailable = async event => {
          this.socket.binary(true).emit("data", event.data)
        }
        this.mediaRecorder.onerror = () => {
          this.socket.close()
        }
      })

      this.socket.on("disconnect", () => {
        console.log("disconncted us......")
      })

      this.socket.on("transcript", data => {
        console.log("got transcript data", data)
        if (data && data.transcription) {
          this.mapTranscription(data)
        }
      })
    },
    stopRecording() {
      this.mediaRecorder &&
        this.mediaRecorder.state !== "inactive" &&
        this.mediaRecorder.stop()

      this.socket && this.socket.off()
      this.mediaRecorder = null
    },
    countRecordingTime() {
      clearInterval(this.recordingTimeIntervalId)
      this.recordingTime = 0
      this.recordingTimeIntervalId = setInterval(() => {
        this.recordingTime += 500
      }, 500)
    },
    stopRecordingTime() {
      clearInterval(this.recordingTimeIntervalId)
    },
    mapTranscription(data) {
      const getUserPayload = userID =>
        this.onlineUsersArray.find(({ id }) => id === userID)

      const userPayload = getUserPayload(data.userID)
      if (this.getCurrentMode === "social") {
        if (userPayload && userPayload.selected) {
          this.transcript = data.transcription
          this.addToTranscriptions({ ...data, ...userPayload })
        }
      } else {
        if (
          (userPayload && userPayload.teamID === this.user.teamID) ||
          (userPayload && userPayload.id === this.user.id) ||
          (userPayload && userPayload.role === Role.Host)
        ) {
          this.transcript = data.transcription
          this.addToTranscriptions({ ...data, ...userPayload })
        }
      }
    },
    addToTranscriptions(value) {
      if (this.transcriptions.length > MAX_TRANSCRIPTIONS_LENGTH) {
        this.transcriptions = [
          ...this.transcriptions.slice(TRANSCRIPTIONS_OFFSET),
          value
        ]
      } else {
        this.transcriptions.push(value)
      }
    }
  }
}
</script>

<style lang="scss">
.transcript-container {
  position: absolute;
  max-width: 800px;
  min-width: 400px;
  width: fit-content;
  min-height: 40px;
  background: $color-primary-dark;
  border: solid 2px $primary_accent_color;
  border-radius: 10px;
  left: 0;
  right: 0;
  margin: auto;
  bottom: 5px;
  padding: 5px;
  z-index: 444;
  max-height: 100px;
  overflow: auto;
  .transcript {
    position: relative;
    // background-color: black;
    color: white;
    font-size: 13px;
    letter-spacing: 0.1em;
    font-weight: bolder;
    padding: 3px;
    line-height: 7px;
    .username {
      color: yellow;
      font-weight: bolder;
    }
  }
  .actions {
    margin-bottom: -9px;
  }
}
</style>
